import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Stethoscope, HeartPulse, Users, BarChart3, LogOut, Menu, X, Filter, ShieldCheck, CalendarDays, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import api from "../lib/api";

const HeaderSearch = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [openR, setOpenR] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setOpenR(false); return; }
    let active = true;
    api.get(`/patients/search?q=${encodeURIComponent(q.trim())}`).then((r) => {
      if (active) { setResults(r.data); setOpenR(true); }
    }).catch(() => {});
    return () => { active = false; };
  }, [q]);

  const go = (p) => { setQ(""); setResults([]); setOpenR(false); navigate(`/patients?focus=${p._id}`); };

  return (
    <div className="relative w-56 sm:w-80">
      <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
      <Input
        data-testid="global-patient-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpenR(true)}
        onBlur={() => setTimeout(() => setOpenR(false), 150)}
        placeholder="Find patient by ID or name…"
        className="h-9 pl-9"
      />
      {openR && results.length > 0 && (
        <div className="absolute z-40 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden" data-testid="global-search-results">
          {results.map((p) => (
            <button
              key={p._id}
              data-testid={`global-search-result-${p._id}`}
              onMouseDown={() => go(p)}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm flex items-center justify-between gap-3"
            >
              <span className="text-ccc-navy font-medium truncate">{p.name}</span>
              <span className="font-mono text-xs text-slate-400 flex-shrink-0">{p.patient_code || "enquiry"}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/leads", label: "Sales Pipeline", icon: Filter, testid: "nav-leads" },
  { to: "/doctors", label: "Doctors", icon: Stethoscope, testid: "nav-doctors" },
  { to: "/clinicians", label: "Clinicians", icon: HeartPulse, testid: "nav-clinicians" },
  { to: "/patients", label: "Patients", icon: Users, testid: "nav-patients" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/users", label: "User Master", icon: ShieldCheck, testid: "nav-users", adminOnly: true },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-6 border-b border-white/10">
        <Logo className="h-9" light showTag />
        <p className="mt-3 text-[11px] uppercase tracking-widest text-slate-400">Acquisition CRM</p>
      </div>
      <nav className="flex-1 px-3 py-5 space-y-1">
        {nav.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-ccc-blue text-white shadow-lg shadow-ccc-blue/30"
                  : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px]" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 mb-3">
          <div className="h-9 w-9 rounded-full bg-ccc-blue2 flex items-center justify-center text-white text-sm font-semibold">
            {(user?.name || "U").slice(0, 1)}
          </div>
          <div className="min-w-0">
            <p className="text-sm text-white font-medium truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          data-testid="logout-button"
          variant="ghost"
          onClick={logout}
          className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/5"
        >
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#F4F6FB]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-ccc-navy fixed inset-y-0 left-0 z-30">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-ccc-navy">
            <button className="absolute top-4 right-4 text-white" onClick={() => setOpen(false)}>
              <X className="h-5 w-5" />
            </button>
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-200 px-4 lg:px-8 h-16 flex items-center justify-between">
          <button className="lg:hidden text-ccc-navy" onClick={() => setOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-6 w-6" />
          </button>
          <div className="hidden lg:block"><HeaderSearch /></div>
          <div className="flex items-center gap-3">
            <div className="lg:hidden"><HeaderSearch /></div>
            <span className="text-xs text-slate-500 hidden sm:inline">India · FY26</span>
          </div>
        </header>
        <main className="p-4 lg:p-8 max-w-[1500px] mx-auto">{children}</main>
      </div>
    </div>
  );
};
